import { useAnalytics, AnalyticsData } from "@/hooks/useAnalytics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, Eye, TrendingUp, TrendingDown, Minus, Globe, Monitor, FileText, Radio, Clock, ArrowRight, MapPin, MousePointerClick, LogOut, Navigation, Phone, Search, FileX } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, AreaChart, Area } from "recharts";


const SOURCE_COLORS = ["hsl(var(--primary))", "hsl(var(--primary) / 0.65)", "hsl(var(--primary) / 0.35)", "hsl(var(--primary) / 0.2)"];

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min}m ${sec}s`;
};

// ── Section header ──
const SectionHeader = ({ icon: Icon, title }: { icon: React.ElementType; title: string }) => (
  <div className="flex items-center gap-2 pt-2">
    <Icon className="w-4 h-4 text-primary" />
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    <div className="flex-1 h-px bg-border/60" />
  </div>
);

// ── Compact summary stat ──
const StatCard = ({
  icon: Icon,
  label,
  value,
  accent,
  sub,
  animDelay,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  accent?: boolean;
  sub?: React.ReactNode;
  animDelay: number;
}) => (
  <Card
    className={`animate-fade-in overflow-hidden ${accent ? "border-primary/30 bg-primary/5" : ""}`}
    style={{ animationDelay: `${animDelay}ms`, animationFillMode: "both" }}
  >
    <CardContent className="p-3.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${accent ? "text-primary animate-pulse" : "text-muted-foreground"}`} />
        <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      </div>
      <p className={`text-xl font-extrabold leading-none tracking-tight ${accent ? "text-primary" : "text-foreground"}`}>
        {value}
      </p>
      {sub && <div className="mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

// ── Summary cards ──
const SummaryCards = ({ data }: { data: AnalyticsData }) => {
  const visitorChange = data.yesterdayVisitors > 0
    ? Math.round(((data.todayVisitors - data.yesterdayVisitors) / data.yesterdayVisitors) * 100)
    : data.todayVisitors > 0 ? 100 : 0;

  const TrendIcon = visitorChange > 0 ? TrendingUp : visitorChange < 0 ? TrendingDown : Minus;
  const trendColor = visitorChange > 0 ? "text-primary" : visitorChange < 0 ? "text-destructive" : "text-muted-foreground";

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard icon={Radio} label="Teraz online" value={data.activeNow} accent animDelay={0} />
      <StatCard
        icon={Users}
        label="Dziś"
        value={data.todayVisitors}
        animDelay={60}
        sub={
          <div className={`flex items-center gap-0.5 text-[10px] ${trendColor}`}>
            <TrendIcon className="w-2.5 h-2.5" />
            <span>{visitorChange > 0 ? "+" : ""}{visitorChange}% vs wczoraj</span>
          </div>
        }
      />
      <StatCard icon={Eye} label="Odsłony dziś" value={data.todayPageviews} animDelay={120} />
      <StatCard icon={LogOut} label="Bounce rate" value={`${data.bounceRate}%`} animDelay={180} />
      <StatCard icon={Clock} label="Śr. sesja" value={formatDuration(data.avgSessionDuration)} animDelay={240} />
      <StatCard icon={Users} label="Wczoraj" value={data.yesterdayVisitors} animDelay={300} />
      <StatCard icon={Users} label="Wczoraj" value={data.yesterdayVisitors} animDelay={420} />
    </div>
  );
};

// ── Weekly chart with gradient area ──
const WeeklyChart = ({ data }: { data: AnalyticsData }) => (
  <Card className="animate-fade-in" style={{ animationDelay: "200ms", animationFillMode: "both" }}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold">Ostatnie 7 dni</CardTitle>
    </CardHeader>
    <CardContent>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data.weeklyData}>
          <defs>
            <linearGradient id="gradVisitors" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="gradPageviews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
          <XAxis dataKey="date" className="text-xs" tickLine={false} axisLine={false} />
          <YAxis className="text-xs" tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              color: "hsl(var(--foreground))",
              fontSize: "12px",
            }}
          />
          <Legend wrapperStyle={{ fontSize: "12px" }} />
          <Area type="monotone" dataKey="visitors" name="Odwiedzający" fill="url(#gradVisitors)" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3, fill: "hsl(var(--primary))" }} />
          <Area type="monotone" dataKey="pageviews" name="Odsłony" fill="url(#gradPageviews)" stroke="hsl(var(--primary) / 0.4)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

// ── Peak hours chart ──
const PeakHoursChart = ({ data }: { data: AnalyticsData }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
        Godziny szczytu
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data.peakHours}>
          <defs>
            <linearGradient id="gradHours" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
          <XAxis dataKey="hour" className="text-[10px]" interval={3} tickLine={false} axisLine={false} />
          <YAxis className="text-[10px]" tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              color: "hsl(var(--foreground))",
              fontSize: "12px",
            }}
          />
          <Area type="monotone" dataKey="pageviews" name="Odsłony" fill="url(#gradHours)" stroke="hsl(var(--primary))" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);


// ── Ranked list with progress bars ──
const RankedList = ({ title, icon: Icon, items, labelKey, countKey }: {
  title: string;
  icon: React.ElementType;
  items: Record<string, unknown>[];
  labelKey: string;
  countKey: string;
}) => {
  const maxCount = items.length > 0 ? Math.max(...items.map((item) => Number(item[countKey]) || 0)) : 1;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          {items.length === 0 && <p className="text-xs text-muted-foreground py-2">Brak danych</p>}
          {items.map((item, i) => {
            const count = Number(item[countKey]) || 0;
            const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
            return (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="truncate flex-1 text-muted-foreground">
                    <span className="text-foreground font-semibold mr-1.5">{i + 1}.</span>
                    {String(item[labelKey])}
                  </span>
                  <span className="text-foreground font-semibold ml-2 tabular-nums">{count}</span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary/40 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Entry → Exit ──
const PAGE_LABELS: Record<string, string> = {
  "/": "Strona główna",
  "/zglos": "Zgłoś aktualizację",
  "/dla-schronisk": "Dla schronisk",
  "/polityka-prywatnosci": "Polityka prywatności",
  "/admin": "Panel admina",
  "/szybka-aktualizacja": "Szybka aktualizacja",
  "/404": "Nie znaleziono",
};
const friendlyPage = (path: string) => PAGE_LABELS[path] || path;

const EntryExitSection = ({ data }: { data: AnalyticsData }) => {
  const maxCount = data.entryExitPages.length > 0
    ? Math.max(...data.entryExitPages.map(e => e.count))
    : 1;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
          Wejście → Wyjście
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          {data.entryExitPages.length === 0 && <p className="text-xs text-muted-foreground py-2">Brak danych</p>}
          {data.entryExitPages.map((ep, i) => {
            const pct = maxCount > 0 ? (ep.count / maxCount) * 100 : 0;
            return (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5 flex-1 min-w-0 text-muted-foreground">
                    <span className="text-foreground font-semibold shrink-0">{i + 1}.</span>
                    <span className="truncate" title={ep.entry}>{friendlyPage(ep.entry)}</span>
                    <ArrowRight className="w-3 h-3 text-primary/40 shrink-0" />
                    <span className="truncate" title={ep.exit}>{friendlyPage(ep.exit)}</span>
                  </span>
                  <span className="text-foreground font-semibold ml-2 tabular-nums shrink-0">{ep.count}</span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary/40 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Shelter open sources pie ──
const ShelterSourcesChart = ({ data }: { data: AnalyticsData }) => {
  const total = data.shelterOpenSources.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Navigation className="w-3.5 h-3.5 text-muted-foreground" />
          Źródło otwarć schronisk
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={data.shelterOpenSources}
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={70}
              dataKey="count"
              nameKey="source"
              label={({ source, percent }) => `${source} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
              style={{ fontSize: "11px" }}
            >
              {data.shelterOpenSources.map((_, i) => (
                <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        <div className="mt-2 text-[10px] text-muted-foreground text-center">
          Łącznie: {total} otwarć
        </div>
      </CardContent>
    </Card>
  );
};

export const AnalyticsTab = () => {
  const { data, isLoading, error } = useAnalytics();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <p>Nie udało się załadować analityki</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-lg">Analityka strony</h2>
        <Badge variant="outline" className="text-[10px]">Odświeża co minutę</Badge>
      </div>

      {/* ── Podsumowanie ── */}
      <SectionHeader icon={Radio} title="Podsumowanie" />
      <SummaryCards data={data} />

      {/* ── Wykresy ── */}
      <SectionHeader icon={TrendingUp} title="Trendy" />
      <WeeklyChart data={data} />

      <PeakHoursChart data={data} />

      <ShelterSourcesChart data={data} />

      {/* ── Nawigacja ── */}
      <SectionHeader icon={ArrowRight} title="Wejście i wyjście" />
      <EntryExitSection data={data} />

      {/* ── Szczegóły ── */}
      <SectionHeader icon={FileText} title="Rankingi" />
      <div className="grid md:grid-cols-3 gap-4">
        <RankedList title="Popularne strony" icon={FileText} items={data.topPages} labelKey="path" countKey="count" />
        <RankedList title="Źródła ruchu" icon={Globe} items={data.topReferrers} labelKey="referrer" countKey="count" />
        <RankedList title="Urządzenia" icon={Monitor} items={data.deviceBreakdown} labelKey="device" countKey="count" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <RankedList title="Popularne schroniska" icon={MapPin} items={data.popularShelters} labelKey="name" countKey="count" />
        <RankedList title="Najmniej popularne" icon={MapPin} items={data.leastPopularShelters || []} labelKey="name" countKey="count" />
        <RankedList title="Mapa miast" icon={MapPin} items={data.cityHeatmap} labelKey="city" countKey="count" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <RankedList title="Kliknięcia" icon={MousePointerClick} items={data.buttonClicks} labelKey="name" countKey="count" />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              Ostatnio otwierane
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2.5">
              {(!data.recentlyOpenedShelters || data.recentlyOpenedShelters.length === 0) && <p className="text-xs text-muted-foreground py-2">Brak danych</p>}
              {(data.recentlyOpenedShelters || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="truncate flex-1 text-muted-foreground">
                    <span className="text-foreground font-semibold mr-1.5">{i + 1}.</span>
                    {s.name}
                  </span>
                  <span className="text-muted-foreground ml-2 shrink-0">
                    {s.city} · {new Date(s.time).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Konwersje ── */}
      <SectionHeader icon={Phone} title="Konwersje i formularze" />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 [&>:last-child]:lg:col-span-2">
        <RankedList title="Kliknięcia kontaktowe" icon={Phone} items={data.contactClicks || []} labelKey="type" countKey="count" />
        <RankedList title="Szukane bez wyników" icon={Search} items={data.emptySearches || []} labelKey="query" countKey="count" />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FileX className="w-3.5 h-3.5 text-muted-foreground" />
              Formularze
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Wysłane</span>
                <span className="text-foreground font-semibold tabular-nums">{data.submittedForms ?? 0}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Porzucone</span>
                <span className="text-foreground font-semibold tabular-nums">{data.abandonedForms ?? 0}</span>
              </div>
              {(data.submittedForms ?? 0) + (data.abandonedForms ?? 0) > 0 && (
                <div className="flex items-center justify-between text-xs pt-1 border-t border-border">
                  <span className="text-muted-foreground">Konwersja</span>
                  <span className="text-foreground font-semibold tabular-nums">
                    {Math.round(((data.submittedForms ?? 0) / ((data.submittedForms ?? 0) + (data.abandonedForms ?? 0))) * 100)}%
                  </span>
                </div>
              )}
              {(data.formsByShelter?.length ?? 0) > 0 && (
                <>
                  <div className="pt-2 border-t border-border">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Per schronisko</span>
                  </div>
                  {data.formsByShelter.map((s, i) => (
                    <div key={i} className="space-y-0.5">
                      <p className="text-xs text-muted-foreground leading-snug">{s.name}</p>
                      <div className="flex items-center gap-2 text-[11px] tabular-nums">
                        <span className="text-primary">✓ {s.submitted}</span>
                        <span className="text-muted-foreground/60">✗ {Math.max(0, s.opened - s.submitted)}</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
